<template>
    <div class="giftEvents" :class="{ 'giftEvents--orange-bg': orangeBackground }">
        <div v-for="(item, key) in giftActions" :key="key" class="giftEvents__item">
            <div class="giftEvents__content">
                <div v-if="$device.isDesktop && item.hasImage" class="giftEvents__photo giftEvents__photo--hover">
                    <ObjectsGiftImages :item="item" />
                </div>
                <div v-else class="giftEvents__photo">
                    <img
                        v-lazy-load
                        class="giftEvents__img"
                        src="/nuxt-assets/images/lazy-load-error.svg"
                        :data-src="item.icon"
                        :alt="item.header"
                    />
                </div>
                <div>
                    <div
                        v-if="false && item.giftEventId in discountPrices && discountPrices[item.giftEventId] >= 0"
                        class="giftEvents__price"
                    >
                        <div class="giftEvents__price__price" v-html="$formatCurrency(discountPrices[item.giftEventId])"></div>
                        <div class="giftEvents__price__text" v-html="$t('productDetail.giftEvent.withDiscount')"></div>
                    </div>
                    <div class="giftEvents__text">
                        <strong class="text-brand">{{ item.header }}:</strong> <strong>{{ item.title }}</strong>
                        <div
                            v-if="item.subtitle"
                            class="giftEvents__subtitle"
                            v-html="item.subtitle.replace(/<p[^>]*>/g, '').replace(/<\/?p[^>]*>/g, '')"
                        ></div>
                        <a v-if="item.descriptionInProduct" class="giftEvents__more" @click="showModal(item.giftEventId)">{{
                            $t('giftsActions.more')
                        }}</a>
                    </div>
                </div>
            </div>
            <div
                v-if="
                    (item.showCountdown || (parseInt(item.giftEventId + '') === 6715 && locale === 'cz')) &&
                    allowCountdownInGiftEvents &&
                    key === firstGiftWithCountdown
                "
                class="giftEvents__countdown"
            >
                <div class="giftEvents__countdown-wrapper">
                    <div class="giftEvents__countdown-wrapper__title">{{ $t('dailyOffer.end') }}</div>
                    <Countdown />
                </div>
                <div class="giftEvents__tag">
                    {{ $t('productDetail.priceWithCode.continuedTag') }}
                </div>
            </div>
            <Modal
                v-if="item.descriptionInProduct"
                :id="`gifts-modal-` + item.giftEventId"
                ref="modal"
                class="giftEvents__modal"
                :modal-name="`gifts-modal-` + item.giftEventId"
                :title="item.header"
                size="md"
                role="dialog"
                aria-hidden="true"
            >
                <div v-html="item.descriptionInProduct" />
            </Modal>
        </div>
    </div>
</template>

<script lang="ts">
import { PropType } from 'vue'
import Modal from '~/components/Modal.vue'
import ProductGiftAction from '~/models/GiftEvent/ProductGiftAction'
import Countdown from '~/components/elements/Countdown.vue'
import GiftEventsDiscountPriceResponseObject from '~/models/Api/GiftEventsDiscountPriceResponseObject'
import { useBasketStore } from '~/stores/basket'

export default {
    components: {
        Countdown,
        Modal,
    },

    props: {
        giftActions: {
            type: Array as PropType<ProductGiftAction[]>,
            required: true,
        },
        orangeBackground: {
            type: Boolean,
            default: false,
        },
        allowCountdownInGiftEvents: {
            type: Boolean,
            default: true,
        },
        productId: {
            type: Number,
            required: true,
        },
        refreshDiscountPrice: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            discountPrices: [] as number[],
        }
    },

    computed: {
        locale(): string {
            const config = useRuntimeConfig()

            return config.public.locale
        },

        firstGiftWithCountdown(): number {
            return this.giftActions.findIndex((item: ProductGiftAction) => item.showCountdown)
        },
    },

    async mounted() {
        await this.refresh()

        useNuxtApp().$bus.$on('productAddedToBasket', async () => {
            await this.refresh()
        })
    },

    unmounted() {
        useNuxtApp().$bus.$off('productAddedToBasket')
    },

    methods: {
        showModal(giftEventId: number) {
            useNuxtApp().$bus.$emit('showModal', 'gifts-modal-' + giftEventId)
        },

        async refresh() {
            try {
                if (this.refreshDiscountPrice && this.giftActions.length) {
                    const discountPrices = await useNuxtApp().$repository.getGiftEventsDiscountPrice(
                        useBasketStore().uid ?? '',
                        this.giftActions.map((event: ProductGiftAction) => event.giftEventId),
                        [this.productId]
                    )

                    const prices: number[] = []
                    for (const event of this.giftActions) {
                        const foundRow = discountPrices.giftEventsDiscountPrice.find(
                            (e: GiftEventsDiscountPriceResponseObject) => e.giftEventId === Number(event.giftEventId)
                        )
                        if (!foundRow || foundRow.priceWithDiscount === null) {
                            continue
                        }

                        prices[event.giftEventId] = foundRow.priceWithDiscount
                    }

                    this.discountPrices = prices
                }
            } catch (e) {}
        },
    },
}
</script>

<style lang="scss" scoped>
.giftEvents {
    margin: 1.5rem 0 0 0;

    &--orange-bg {
        background: $color-cta-header-light;
        padding: 10px;
        border-radius: 10px;

        .giftEvents__photo {
            background-color: $color-white;
        }
    }
}

.giftEvents__countdown {
    position: relative;
}

.giftEvents__countdown-wrapper {
    margin-top: 10px;
    margin-bottom: 10px;
    display: flex;

    &__title {
        font-weight: 600;
        font-size: 14px;
        margin: auto 0;

        @media (max-width: 768px) {
            width: 35%;
        }
    }

    .countdown {
        font-size: 22px;
        color: $color-cta;
        white-space: nowrap;
        font-weight: 700;
        margin-left: auto;

        @media (max-width: 768px) {
            margin-top: 8px;
        }
    }
}

.giftEvents__tag {
    border-radius: 100px;
    font-weight: 600;
    padding: 5px 12px 5px 12px;
    background: #ffca63;
    display: inline-block;
    position: absolute;
    bottom: -34px;
    font-size: 13px;
    line-height: 25px;
}

.giftEvents__content {
    display: flex;
    gap: 13px;
    align-items: center;
    font-size: 0.875rem;
    //margin-bottom: 0.875rem;
}

.giftEvents__photo {
    background-color: $color-grey;
    border-radius: 10px;
    width: 48px;
    height: 48px;
    flex: 0 0 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    align-self: normal;

    &--hover {
        cursor: help;
    }
}

.giftEvents__img {
    width: auto;
    height: auto;
    max-width: 32px;
    max-height: 32px;
}

.giftEvents__more {
    font-size: 0.75rem;
    text-decoration: underline;
    cursor: pointer;

    &:hover {
        @media (min-width: 980px) {
            text-decoration: none;
        }
    }
}

.giftEvents__text {
    font-size: 14px;
    line-height: 1.188rem;
}

.giftEvents__subtitle {
    font-size: 0.75rem;
    line-height: 1rem;
    margin: 0.25rem 0 0;
}

.giftEvents__modal {
    &:deep(.rc-block.rc-gift) {
        margin-bottom: 0;
    }
}

.giftEvents__price {
    font-size: 14px;
    font-weight: 600;
    display: flex;
    margin-bottom: 10px;

    &__price {
        color: #e6007e;
        font-size: 26px;
    }

    &__text {
        line-height: 16px;
        margin-left: 10px;
        align-content: center;
        margin-top: 10px;
    }
}
</style>
